import { formatJobNumber, inboxListSchema } from '@fieldops/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { cn } from '../../lib/cn.js'
import { formatDateTime, formatTime } from '../../lib/format.js'
import { useJobOverlay } from '../../lib/job-overlay.js'
import { useIsTablet } from '../../lib/media.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { useUrlState } from '../../lib/url-state.js'
import { quoted } from '../compose/text.js'
import { FileGallery, attachmentViewerFile, useFileViewer } from '../FileViewer.js'
import { Icon } from '../Icon.js'
import { EmailCompose } from '../email/EmailCompose.js'
import { SmsCompose } from '../job-card/SmsCompose.js'
import { EmptyState, ErrorState, SkeletonRows } from '../ui.js'
import { FieldScreen, PushedPage, ScreenHeader, cardClass } from './kit.js'

function when(value: Date | string): string {
  const date = new Date(value)
  if (date.toDateString() === new Date().toDateString()) return formatTime(date)
  return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })
}

function isText(channel: string): boolean {
  return channel === 'SMS' || channel === 'MMS'
}

/**
 * The inbox as a list of conversations, the way a phone's own messages app
 * draws one. Opening a message slides it over the list on a phone and fills
 * the right-hand pane on an iPad; either way it is `?selected=`, the same
 * address the office inbox uses.
 */
export function FieldInbox() {
  const trpc = useTRPC()
  const tablet = useIsTablet()
  const [params, setParams] = useUrlState(inboxListSchema)
  const { data, isPending, error, refetch } = useQuery(trpc.inbox.list.queryOptions(params))
  const unread = useQuery(trpc.inbox.unreadCount.queryOptions())

  const select = (id: string | undefined) => setParams({ selected: id }, { replace: !tablet })

  return (
    <FieldScreen>
      <ScreenHeader
        eyebrow={unread.data ? `${unread.data} unread` : 'All caught up'}
        title="Inbox"
      />

      {error ? (
        <div className={cardClass}>
          <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
        </div>
      ) : isPending ? (
        <div className={cardClass}>
          <SkeletonRows rows={6} />
        </div>
      ) : data.items.length === 0 ? (
        <div className={cn(cardClass, 'py-4')}>
          <EmptyState title="Nothing in the inbox" />
        </div>
      ) : (
        <ul className={cn(cardClass, 'divide-y divide-ink-200')}>
          {data.items.map((message) => {
            const fresh = message.status === 'UNREAD'
            const active = params.selected === message.id
            return (
              <li key={message.id}>
                <button
                  type="button"
                  onClick={() => select(message.id)}
                  aria-current={active ? 'true' : undefined}
                  className={cn(
                    'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors active:bg-ink-50',
                    active && 'bg-brand-50',
                  )}
                >
                  <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-md bg-ink-100 text-ink-600">
                    <Icon name={isText(message.channel) ? 'activity' : 'mail'} className="size-[18px]" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2">
                      <span
                        className={cn(
                          'min-w-0 flex-1 truncate text-[16px] text-ink-900',
                          fresh ? 'font-bold' : 'font-medium',
                        )}
                      >
                        {message.fromName ?? message.client?.name ?? message.fromAddress}
                      </span>
                      <span className="shrink-0 text-[13px] text-ink-500">
                        {when(message.receivedAt)}
                      </span>
                    </span>
                    <span
                      className={cn(
                        'mt-0.5 line-clamp-2 text-[14px] leading-snug',
                        fresh ? 'text-ink-800' : 'text-ink-500',
                      )}
                    >
                      {message.subject && !isText(message.channel)
                        ? `${message.subject} — `
                        : ''}
                      {message.strippedText ?? message.bodyText ?? ''}
                    </span>
                    <span className="mt-1 block truncate text-[12px] text-ink-400">
                      {isText(message.channel) ? 'Text' : 'Email'}
                      {message.client ? ` · ${message.client.name}` : ''}
                      {message.jobId ? ' · On a job' : ''}
                    </span>
                  </span>
                  {fresh ? (
                    <span
                      aria-label="Unread"
                      className="mt-2 size-2 shrink-0 rounded-[2px] bg-brand-600"
                    />
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {!tablet && params.selected ? (
        <PushedPage
          key={params.selected}
          labelledBy="field-thread-title"
          safeTop={false}
          onClose={() => select(undefined)}
        >
          {(back) => <FieldThread id={params.selected!} onBack={back} />}
        </PushedPage>
      ) : null}
    </FieldScreen>
  )
}

/**
 * One message, read as a conversation bubble, with its job and a reply.
 *
 * Reply opens the one compose window (rule 40): an email reply is
 * `EmailCompose` threaded on this message, a text reply is `SmsCompose`, which
 * hands off to the phone's own messages app.
 */
export function FieldThread({ id, onBack }: { id: string; onBack?: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { can } = useSession()
  const { openJob } = useJobOverlay()
  const [replying, setReplying] = useState(false)

  const { data, isPending, error, refetch } = useQuery(trpc.inbox.byId.queryOptions({ id }))
  // A customer's photos of what they found, looked at where they arrived.
  const { open: openFile, viewer } = useFileViewer()
  const files = (data?.attachments ?? []).map((row) => attachmentViewerFile(row.attachment))
  const invalidate = () => void queryClient.invalidateQueries(trpc.inbox.pathFilter())
  const setStatus = useMutation(trpc.inbox.setStatus.mutationOptions({ onSuccess: invalidate }))
  const convert = useMutation(
    trpc.inbox.convertToJob.mutationOptions({
      onSuccess: (result) => {
        invalidate()
        openJob(result.jobId)
      },
    }),
  )

  // Reading a message on a phone is reading it; the office inbox leaves this
  // to a button because a dispatcher skims a pane without meaning to.
  const marked = useRef(false)
  useEffect(() => {
    if (marked.current || !data || data.status !== 'UNREAD' || !can('INBOX_MANAGE')) return
    marked.current = true
    setStatus.mutate({ id: data.id, status: 'READ' })
  }, [data, can, setStatus])

  if (error) return <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
  if (isPending) return <SkeletonRows rows={4} />

  const text = isText(data.channel)
  const outbound = data.direction === 'OUTBOUND'
  const who = data.fromName ?? data.client?.name ?? data.fromAddress
  const replyTo = outbound ? (data.toAddresses[0] ?? data.fromAddress) : data.fromAddress

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header
        className={cn(
          'flex shrink-0 items-center gap-2 bg-white/90 px-2 pb-2 shadow-[inset_0_-1px_0_var(--color-ink-200)] backdrop-blur-xl',
          onBack ? 'pt-[calc(var(--safe-top)+6px)]' : 'pt-2',
        )}
      >
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex h-9 shrink-0 items-center gap-0.5 rounded-md px-1.5 text-[16px] font-medium text-brand-700"
          >
            <Icon name="back" className="size-5" />
            Inbox
          </button>
        ) : (
          <span className="w-2" />
        )}
        <div className="min-w-0 flex-1 text-center">
          <p id="field-thread-title" className="truncate text-[16px] font-semibold text-ink-900">{who}</p>
          <p className="truncate text-[12px] text-ink-500">
            {text ? 'Text' : 'Email'} · {data.fromAddress}
          </p>
        </div>
        {data.job ? (
          <button
            type="button"
            onClick={() => openJob(data.job!.id)}
            className="shrink-0 rounded-md bg-brand-50 px-3 py-1.5 font-mono text-[12.5px] font-semibold text-brand-700 ring-1 ring-brand-200 ring-inset"
          >
            {formatJobNumber(data.job)}
          </button>
        ) : can('INBOX_MANAGE') && can('JOB_EDIT') && data.client ? (
          <button
            type="button"
            disabled={convert.isPending}
            onClick={() => convert.mutate({ messageId: data.id })}
            className="shrink-0 rounded-md bg-brand-500 px-3 py-1.5 text-[13px] font-semibold text-ink-900 disabled:opacity-70"
          >
            Create job
          </button>
        ) : (
          <span className="w-16" />
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <p className="mb-3 text-center text-[12px] font-medium text-ink-500">
          {formatDateTime(data.receivedAt)}
        </p>
        <div className={cn('flex', outbound ? 'justify-end' : 'justify-start')}>
          <div
            className={cn(
              'max-w-[85%] rounded-lg px-3.5 py-2.5 text-[15px] leading-snug whitespace-pre-wrap',
              outbound
                ? 'rounded-br-sm bg-ink-900 text-white'
                : 'rounded-bl-sm bg-white text-ink-900 ring-1 ring-ink-200 ring-inset',
            )}
          >
            {data.subject && !text ? (
              <p className={cn('mb-1 font-semibold', outbound ? 'text-white' : 'text-ink-900')}>
                {data.subject}
              </p>
            ) : null}
            {data.strippedText ?? data.bodyText ?? '(no message body)'}
          </div>
        </div>
        {files.length > 0 ? (
          <FileGallery
            files={files}
            onOpen={(file) => openFile(files, file.id)}
            className={cn('mt-2 max-w-[85%]', outbound && 'ml-auto')}
          />
        ) : null}
        {viewer}
        {convert.error ? (
          <p role="alert" className="mt-3 text-[13px] text-red-700">
            {errorMessage(convert.error)}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-2 bg-white/92 px-3 pt-2.5 pb-[calc(var(--safe-bottom)+10px)] shadow-[inset_0_1px_0_var(--color-ink-200)] backdrop-blur-xl">
        <button
          type="button"
          onClick={() => setReplying(true)}
          className="flex h-11 min-w-0 flex-1 items-center rounded-md bg-ink-100 px-4 text-left text-[15px] text-ink-500 ring-1 ring-ink-200 ring-inset"
        >
          {text ? 'Reply by text…' : 'Reply by email…'}
        </button>
        <button
          type="button"
          aria-label="Reply"
          onClick={() => setReplying(true)}
          className="grid size-11 shrink-0 place-items-center rounded-md bg-brand-500 text-ink-900"
        >
          <Icon name="send" className="size-5" />
        </button>
      </div>

      {text ? (
        <SmsCompose
          open={replying}
          onClose={() => setReplying(false)}
          contactName={who}
          phone={replyTo}
          jobId={data.jobId}
          replyToMessageId={data.id}
        />
      ) : (
        <EmailCompose
          open={replying}
          onClose={() => setReplying(false)}
          title="Reply"
          jobId={data.jobId}
          clientId={data.client?.id}
          replyToMessageId={data.id}
          files={data.attachments.map((row) => ({
            id: row.attachment.id,
            name: row.attachment.filename,
          }))}
          initial={{
            to: [replyTo],
            subject: data.subject?.toLowerCase().startsWith('re:')
              ? data.subject
              : `Re: ${data.subject ?? '(no subject)'}`,
            body: quoted(data),
          }}
        />
      )}
    </div>
  )
}
